import * as THREE from 'three';

const N = 18;
/** Seconds a sample stays in the ribbon. */
const LIFE = 0.16;

const VERT = /* glsl */ `
attribute float aAge;
attribute float aSide;
varying float vAge;
varying float vSide;
void main() {
  vAge = aAge;
  vSide = aSide;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uFade;
varying float vAge;
varying float vSide;
void main() {
  float a = clamp(1.0 - vAge, 0.0, 1.0);
  a = a * a;
  // Bright core toward the blade tip (side 1), soft toward the hilt.
  float edge = smoothstep(0.0, 1.0, vSide);
  vec3 col = mix(uColor, vec3(1.0), edge * a * 0.6) * (1.2 + edge * 2.2);
  gl_FragColor = vec4(col, a * (0.25 + 0.75 * edge) * uFade);
}`;

/**
 * Additive ribbon swept by a weapon (hilt → tip) while an attack clip plays:
 * a ring buffer of the last samples, aged out after 0.16 s.
 */
export class WeaponTrail {
  readonly mesh: THREE.Mesh;
  private pos = new Float32Array(N * 2 * 3);
  private age = new Float32Array(N * 2);
  private times = new Float32Array(N);
  private count = 0;
  private time = 0;
  private geo: THREE.BufferGeometry;
  private mat: THREE.ShaderMaterial;
  private fade = 0;

  constructor(color: THREE.Color) {
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('aAge', new THREE.BufferAttribute(this.age, 1).setUsage(THREE.DynamicDrawUsage));
    const side = new Float32Array(N * 2);
    for (let i = 0; i < N; i++) {
      side[i * 2] = 0;
      side[i * 2 + 1] = 1;
    }
    this.geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    const idx: number[] = [];
    for (let i = 0; i < N - 1; i++) {
      const a = i * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    this.geo.setIndex(idx);
    this.geo.setDrawRange(0, 0);
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uColor: { value: color.clone() }, uFade: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 12;
    this.mesh.name = 'weapon_trail';
  }

  set color(c: THREE.Color) {
    (this.mat.uniforms.uColor as THREE.IUniform<THREE.Color>).value.copy(c);
  }

  /**
   * Adds a sample while `active`; the ribbon keeps aging out otherwise.
   * Returns false once fully faded (the owner may release it).
   */
  update(dt: number, active: boolean, hilt: THREE.Vector3, tip: THREE.Vector3): boolean {
    this.time += dt;
    this.fade = active ? Math.min(1, this.fade + dt * 20) : Math.max(0, this.fade - dt * 5);
    if (active) {
      // Shift history and write the newest sample at the front.
      this.pos.copyWithin(6, 0, (N - 1) * 6);
      this.times.copyWithin(1, 0, N - 1);
      hilt.toArray(this.pos, 0);
      tip.toArray(this.pos, 3);
      this.times[0] = this.time;
      this.count = Math.min(N, this.count + 1);
    }
    let live = 0;
    for (let i = 0; i < this.count; i++) {
      const k = (this.time - (this.times[i] as number)) / LIFE;
      this.age[i * 2] = k;
      this.age[i * 2 + 1] = k;
      if (k < 1) live = i + 1;
    }
    this.count = live;
    (this.mat.uniforms.uFade as THREE.IUniform<number>).value = this.fade;
    this.geo.setDrawRange(0, Math.max(0, live - 1) * 6);
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.getAttribute('aAge') as THREE.BufferAttribute).needsUpdate = true;
    return live > 0 || this.fade > 0;
  }

  dispose(): void {
    this.geo.dispose();
    this.mat.dispose();
  }
}
